/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-props-no-spreading */
import { anyTextRegex, onlyNumbersRegex, taxIdRegex } from '@constants/general';
import { Alert, FormControl, Grid, TextField, Typography } from '@mui/material';
import { validateSpaces, validatePoBox } from '@utils/validations';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@efexteam/components';
import {
  CountryAutocomplete,
  CountryValue,
} from '@efex-components/CountryAutocomplete';
import { CountryCodes } from '@efex-components/common/Form/config';
import { useFormData } from '../context';
import {
  ADDRESS_MAX_LENGTH,
  CITY_MAX_LENGTH,
  POST_CODE_MAX_LENGTH,
  STATE_MAX_LENGTH,
} from '../constants';

interface IAddressData {
  formStep: number;
  nextFormStep: () => void;
  prevStep: () => void;
}

interface FormData {
  companyName?: string;
  country: string;
  address: string;
  city: string;
  state: string;
  postCode: string;
  contactPhone: string;
  taxId: string;
}

const AddressData = ({ formStep, nextFormStep, prevStep }: IAddressData) => {
  const { t } = useTranslation();
  const { setFormValues, data } = useFormData();

  const [currentCountry, setCurrentCountry] = useState<
    CountryValue | undefined
  >(data.country as CountryValue);
  const [isUSAOrSwiftCountry, setIsUSAOrSwiftCountry] =
    useState<boolean>(false);

  const {
    handleSubmit,
    formState: { errors },
    register,
    setValue,
    control,
    formState,
  } = useForm<FormData>({ mode: 'all' });

  const onSubmit = handleSubmit((values) => {
    if (typeof currentCountry !== 'number') {
      setFormValues({ ...values, countryId: undefined });
    } else {
      setFormValues({
        ...values,
        country: CountryCodes.USA,
        countryId: currentCountry,
      });
    }
    nextFormStep();
  });

  const handleBack = () => {
    prevStep();
  };

  const handleCountryChange = (
    country: CountryValue,
    isUSAOrSwift: boolean
  ) => {
    if (country === '') {
      setCurrentCountry(undefined);
      setIsUSAOrSwiftCountry(false);
      setValue('country', '', { shouldValidate: true });
    } else {
      setCurrentCountry(country);
      setIsUSAOrSwiftCountry(isUSAOrSwift);
      setValue('country', String(country), { shouldValidate: true });
    }
  };

  useEffect(() => {
    setValue('companyName', data.companyName || '');
    setValue('address', data.address || '');
    setValue('city', data.city || '');
    setValue('state', data.state || '');
    setValue('postCode', data.postCode || '');
    setValue('contactPhone', data.contactPhone || '');

    if (data.country) {
      setValue('country', data.country, { shouldValidate: true });
      setCurrentCountry(data.country as CountryValue);
      setIsUSAOrSwiftCountry(
        data.country === CountryCodes.USA || typeof data.country === 'number'
      );
    } else {
      setValue('country', '', { shouldValidate: true });
      setCurrentCountry(undefined);
      setIsUSAOrSwiftCountry(false);
    }
  }, [data]);

  return (
    <>
      <Typography variant="h6" className="!my-4 text-center">
        {t('ccProviderTitle')}
      </Typography>

      <form onSubmit={onSubmit} autoComplete="off">
        <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2, md: 3 }}>
          {data.entity === 'Company' && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <TextField
                  id="companyName"
                  label={t('companyName')}
                  error={!!errors.companyName}
                  {...register('companyName', {
                    required: true,
                    pattern: {
                      value: /^[a-zA-Z0-9. ]*$/i,
                      message: '',
                    },
                    validate: {
                      spaces: (value) => validateSpaces(value, t),
                    },
                  })}
                />
                {errors.companyName &&
                  errors.companyName.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                {errors.companyName &&
                  errors.companyName.type === 'pattern' && (
                    <p className="text-orange-600 text-xs">
                      {t('ccErrorNoSpecialCharacters')}
                    </p>
                  )}
                {errors.companyName && errors.companyName.type === 'spaces' && (
                  <p className="text-orange-600 text-xs">
                    {errors.companyName.message as string}
                  </p>
                )}
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <CountryAutocomplete
                name="country"
                control={control}
                errors={errors}
                formState={formState}
                initialCountry={data.country as CountryValue}
                onCountryChange={handleCountryChange}
                rules={{
                  required: true,
                  validate: {
                    notEmpty: (value: string) =>
                      value !== '' || t('defaultError'),
                  },
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} className="!pt-2">
            <Alert severity="warning" className="w-full !mb-5 !mt-1">
              {t('ccCompanyNameHelper')}
            </Alert>
          </Grid>

          {isUSAOrSwiftCountry && (
            <>
              <Grid item xs={12} className="pt-2">
                <Alert severity="info" className="w-full mb-5 mt-1">
                  {t('ccStakeAddressMessage')}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    id="address"
                    label={t('ccAddress')}
                    error={!!errors.address}
                    {...register('address', {
                      required: true,
                      maxLength: ADDRESS_MAX_LENGTH,
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                        poBox: (value) => validatePoBox(value, t),
                      },
                    })}
                  />
                  {errors.address && errors.address.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.address && errors.address.type === 'maxLength' && (
                    <p className="text-orange-600 text-xs">
                      {t('ccAddressMaxLength')}
                    </p>
                  )}
                  {errors.address && errors.address.type === 'spaces' && (
                    <p className="text-orange-600 text-xs">
                      {errors.address.message as string}
                    </p>
                  )}
                  {errors.address && errors.address.type === 'poBox' && (
                    <p
                      className="text-orange-600 text-xs"
                      aria-describedby="address-error"
                    >
                      {errors.address.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} className="!hidden">
                <FormControl fullWidth>
                  <Typography variant="subtitle2">
                    {t('onbTaxIdUSA')}
                  </Typography>
                  <div className="flex w-full">
                    <TextField
                      autoComplete="off"
                      type="text"
                      className="custom-textfield w-[90%]"
                      size="small"
                      error={!!errors?.taxId}
                      helperText="XX-XXXXXXX"
                      placeholder="XX-XXXXXXX"
                      {...register('taxId', {
                        required: false, // TODO: Change to true
                        pattern: {
                          value: taxIdRegex,
                          message: '',
                        },
                        validate: {
                          spaces: (value) => validateSpaces(value, t),
                        },
                      })}
                    />
                  </div>
                  {errors &&
                    errors.taxId &&
                    errors.taxId.type === 'required' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                  {errors &&
                    errors.taxId &&
                    errors.taxId.type === 'pattern' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                  {errors && errors.taxId && errors.taxId.type === 'spaces' && (
                    <p className="text-orange-600 text-xs mt-1">
                      {errors.taxId.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    id="city"
                    label={t('city')}
                    error={!!errors.city}
                    {...register('city', {
                      required: true,
                      maxLength: CITY_MAX_LENGTH,
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                      },
                    })}
                  />
                  {errors.city && errors.city.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.city && errors.city.type === 'maxLength' && (
                    <p className="text-orange-600 text-xs">
                      {t('ccCityMaxLength')}
                    </p>
                  )}
                  {errors.city && errors.city.type === 'spaces' && (
                    <p className="text-orange-600 text-xs">
                      {errors.city.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    id="state"
                    label={t('state')}
                    error={!!errors.state}
                    {...register('state', {
                      required: true,
                      maxLength: STATE_MAX_LENGTH,
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                      },
                    })}
                  />
                  {errors.state && errors.state.type === 'required' && (
                    <p className="text-orange-600 text-xs">
                      {t('defaultError')}
                    </p>
                  )}
                  {errors.state && errors.state.type === 'maxLength' && (
                    <p className="text-orange-600 text-xs">
                      {t('ccStateMaxLength')}
                    </p>
                  )}
                  {errors.state && errors.state.type === 'spaces' && (
                    <p className="text-orange-600 text-xs">
                      {errors.state.message as string}
                    </p>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    id="postCode"
                    label={t('postCode')}
                    error={!!errors.postCode}
                    {...register('postCode', {
                      required: true,
                      maxLength: POST_CODE_MAX_LENGTH,
                      pattern: {
                        value:
                          currentCountry === 'USA'
                            ? onlyNumbersRegex
                            : anyTextRegex,
                        message: '',
                      },
                      validate: {
                        spaces: (value) => validateSpaces(value, t),
                      },
                    })}
                  />
                  {errors &&
                    errors.postCode &&
                    errors.postCode.type === 'required' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('defaultError')}
                      </p>
                    )}
                  {errors &&
                    errors.postCode &&
                    errors.postCode.type === 'pattern' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {t('onbOnlyNumberError')}
                      </p>
                    )}
                  {errors.postCode && errors.postCode.type === 'maxLength' && (
                    <p className="text-orange-600 text-xs mt-1">
                      {t('ccPostCodeMaxLength')}
                    </p>
                  )}
                  {errors &&
                    errors.postCode &&
                    errors.postCode.type === 'spaces' && (
                      <p className="text-orange-600 text-xs mt-1">
                        {errors.postCode.message as string}
                      </p>
                    )}
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          className="flex justify-between !flex-col md:!flex-row !mt-4"
        >
          {formStep > 0 && (
            <Button
              appearance="secondary"
              className="mt-4"
              size="large"
              onClick={handleBack}
              type="button"
            >
              {t('backBtn')}
            </Button>
          )}
          <Button
            appearance="primary"
            className="mt-4"
            size="large"
            type="submit"
          >
            {t('continue')}
          </Button>
        </Grid>
      </form>
    </>
  );
};

export default AddressData;
